import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ArrowRight, MapPin, Phone, Send, Mail, MessageCircle, ChevronDown } from 'lucide-react';

interface ClothingHowItWorksProps {
  isOpen: boolean;
  onClose: () => void;
  onStartOrder: () => void;
}

const clothingColors = {
  accent: '#64a70b',
  dark: '#183028',
  secondary: '#1e3a2f',
};

const steps = [
  {
    number: 1,
    title: 'Submit Your Quote Request',
    description: 'Browse our online product range or visit our showroom to choose your products. Need help choosing? Reach out to a member of our team.',
  },
  {
    number: 2,
    title: 'Design and Customise',
    description: 'Our team will work with you on your artwork. Choose from our Professional Design Service, Artwork Check & Set-Up, or supply your own Print Ready Files.',
  },
  {
    number: 3,
    title: 'Order Confirmation',
    description: 'After you confirm your final artwork proof, we will progress your order to our production department.',
    note: 'Please note: payment is due before production begins.',
  },
  {
    number: 4,
    title: 'Production and Delivery',
    description: 'Production usually takes 5–10 working days. Want it sooner? Talk to us! We\'ll notify you when your order is dispatched or ready for collection.',
  },
];

const ClothingHowItWorks: React.FC<ClothingHowItWorksProps> = ({
  isOpen,
  onClose,
  onStartOrder,
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-3xl max-h-[90vh] mx-4 rounded-2xl overflow-hidden shadow-2xl"
            style={{ backgroundColor: clothingColors.dark }}
          >
            {/* Background texture */}
            <div
              className="absolute inset-0 opacity-20 pointer-events-none"
              style={{
                backgroundImage: 'url(/BlackTextureBackground.webp)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            {/* Content */}
            <div className="relative z-10 p-6 md:p-10 overflow-y-auto max-h-[90vh] scrollbar-clothing">
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-8"
              >
                <h2 className="hearns-font text-3xl md:text-4xl text-white mb-3">
                  How to Order
                </h2>
                <p className="neuzeit-light-font text-base text-white/70 max-w-xl mx-auto">
                  Four simple steps to complete your order.
                </p>
              </motion.div>

              {/* Steps - Vertical Timeline */}
              <div className="relative max-w-2xl mx-auto mb-10">
                {steps.map((step, index) => (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + index * 0.1 }}
                    className="relative flex gap-5"
                  >
                    {/* Timeline connector */}
                    <div className="flex flex-col items-center">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 border-2 relative z-10"
                        style={{
                          backgroundColor: clothingColors.dark,
                          borderColor: clothingColors.accent,
                        }}
                      >
                        <span
                          className="hearns-font text-xl"
                          style={{ color: clothingColors.accent }}
                        >
                          {step.number}
                        </span>
                      </div>

                      {index < steps.length - 1 && (
                        <div className="flex flex-col items-center">
                          <div
                            className="w-0.5 h-8"
                            style={{ backgroundColor: clothingColors.accent }}
                          />
                          <ChevronDown
                            className="w-4 h-4 -mt-1"
                            style={{ color: clothingColors.accent }}
                          />
                          <div
                            className="w-0.5 h-8"
                            style={{ backgroundColor: clothingColors.accent }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Step content */}
                    <div className={`pt-1 ${index < steps.length - 1 ? 'pb-6' : ''}`}>
                      <div
                        className="embossing-font text-[10px] uppercase tracking-[0.2em] mb-1"
                        style={{ color: clothingColors.accent }}
                      >
                        Step {step.number}
                      </div>
                      <h3 className="hearns-font text-xl text-white mb-2">
                        {step.title}
                      </h3>
                      <p className="neuzeit-light-font text-sm text-white/70 leading-relaxed">
                        {step.description}
                      </p>
                      {step.note && (
                        <p className="neuzeit-font text-xs mt-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 italic text-white/60">
                          💡 {step.note}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Action buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="space-y-3"
              >
                {/* Primary CTA */}
                <button
                  onClick={() => {
                    onClose();
                    onStartOrder();
                  }}
                  className="w-full h-14 rounded-[12px] neuzeit-font font-semibold text-base text-white transition-all duration-300 flex items-center justify-center gap-3 hover:shadow-xl hover:scale-[1.01]"
                  style={{
                    backgroundColor: clothingColors.accent,
                    boxShadow: `0 8px 25px ${clothingColors.accent}40`,
                  }}
                >
                  <Send className="w-5 h-5" />
                  Submit Quote Request
                  <ArrowRight className="w-4 h-4" />
                </button>

                {/* Secondary CTAs */}
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href="https://maps.google.com/?q=Outpost+Custom+23+Lisle+Avenue+Kidderminster+DY11+7DF"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-12 rounded-[10px] border border-white/20 neuzeit-font text-sm text-white/80 transition-all duration-200 hover:border-[#64a70b] hover:text-white flex items-center justify-center gap-2"
                  >
                    <MapPin className="w-4 h-4" />
                    Visit our Showroom
                  </a>
                  <a
                    href="tel:01562227117"
                    className="h-12 rounded-[10px] border border-white/20 neuzeit-font text-sm text-white/80 transition-all duration-200 hover:border-[#64a70b] hover:text-white flex items-center justify-center gap-2"
                  >
                    <Phone className="w-4 h-4" />
                    Give Us a Call
                  </a>
                  <a
                    href="https://wa.me/441562227117"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-12 rounded-[10px] border border-white/20 neuzeit-font text-sm text-white/80 transition-all duration-200 hover:border-[#64a70b] hover:text-white flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp Us
                  </a>
                  <a
                    href="mailto:info@outpostcustom.com"
                    className="h-12 rounded-[10px] border border-white/20 neuzeit-font text-sm text-white/80 transition-all duration-200 hover:border-[#64a70b] hover:text-white flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Email Us
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ClothingHowItWorks;
